import firebase from "firebase"
import { db, auth } from "../../../utils/firebase"
import * as mailTypes from "../../../utils/exports/mail-types"
import { sendEmail } from "../../../utils/mail"

// The "Golf Holidays Direct" shared sales account. When a quote is sent by this
// account we send from the generic sales identity rather than the agent's own.
const GHD_SALES_ACCOUNT_ID = "CGOHCdUwFAOe13OSTY2D9qdO2LX2"

// Single source of truth for emailing a generated quote PDF to a client.
//
// This is the per-package send that used to live inline in the wizard's
// Summary stage (stages/send.js). It has been extracted so it can be reused
// BOTH from the build flow and from the post-generation tee-time editor on the
// enquiry Quotes tab (slice 6) when an agent regenerates + resends a quote.
//
// Behaviour is identical to the previous inline implementation: it emails the
// GENERATED_QUOTE template with the PDF attached and writes a QUOTE_SENT log
// onto the client account.
//
// Input:
//   {
//     reference,        // the package reference (e.g. GHDQ-XXXXXX)
//     pdfBytesBase64,   // the regenerated PDF as a base64 string
//     clientID,         // clients/{id} the quote belongs to (for the log)
//     clientName,       // "First Last" for the email greeting
//     clientEmail,      // recipient
//     agent,            // { id, name, first_name, phone, email }
//   }
export const sendQuoteEmail = async ({ reference, pdfBytesBase64, clientID, clientName, clientEmail, agent }) => {
	// Send the client email (same shape/template as the build flow's send stage)
	await sendEmail({
		template: mailTypes.GENERATED_QUOTE.template,
		subject: `Golf Holidays Direct Quote - Ref: ${reference}`,
		from: {
			name: agent.id === GHD_SALES_ACCOUNT_ID ? "Golf Holidays Direct" : agent.name,
			email: agent.id === GHD_SALES_ACCOUNT_ID ? "sales@golfholidaysdirect.com" : agent.email,
		},
		user: {
			name: clientName,
			email: clientEmail,
		},
		mail: {
			name: clientName,
			agent: agent.name,
			agent_phone: agent.phone,
			agent_email: agent.email,
		},
		attachments: [
			{
				type: "application/pdf",
				name: `${reference} - Golf Holidays Direct Quote.pdf`,
				content: pdfBytesBase64,
			},
		],
	})

	// Write a log onto the client account for the quote send
	await db.collection(`clients/${clientID}/logs`).add({
		badge: "INFO",
		type: "QUOTE_SENT",
		user: auth.currentUser.uid,
		message: `The generated quote (${reference}) was sent to ${clientEmail}`,
		created: firebase.firestore.FieldValue.serverTimestamp(),
	})
}

// Resolve the current sales agent's details for inclusion in the send. Kept
// here alongside the sender so callers don't re-implement the lookup shape.
export const resolveCurrentAgent = async () => {
	return db
		.doc(`users/${auth.currentUser.uid}`)
		.get()
		.then((agentDoc) => {
			const { first_name, last_name, phone, email } = agentDoc.data()
			return { id: agentDoc.id, name: `${first_name} ${last_name}`, first_name, phone, email }
		})
}
